import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import Pagination from '../components/Pagination';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const PAGE_SIZE = 25;
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'all', label: 'All' },
];

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: '"Baikal Book"',
};

const buttonStyle = {
  border: 'none',
  borderRadius: '8px',
  padding: '10px 12px',
  fontFamily: '"Baikal Book"',
  fontWeight: 700,
  cursor: 'pointer',
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  try {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (error) {
    return 'N/A';
  }
};

const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getUserName = (user) => {
  const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
  return name || user?.email || 'Unknown user';
};

const isPreviewableReceiptImage = (receipt) => {
  const contentType = (receipt?.content_type || '').toLowerCase();
  if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'].includes(contentType)) {
    return false;
  }
  return !!receipt?.receipt_url;
};

const getReceiptStatusLabel = (receipt) => {
  if (!receipt?.auto_approved) {
    return receipt?.status || 'unknown';
  }
  if (receipt.status === 'declined' || receipt.reviewed_by) {
    return `${receipt.status} (manually)`;
  }
  return `${receipt.status} (auto)`;
};

const getDefaultPointsForReceipt = (receipt) => {
  const amount = Number(receipt?.transaction_amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    return '';
  }
  return String(Math.floor(amount));
};

const SuperuserReceiptReview = ({ scope = 'superuser' }) => {
  const isStaffScope = scope === 'staff';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, declined: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [autoApprove, setAutoApprove] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [pointsByReceipt, setPointsByReceipt] = useState({});
  const [merchantByReceipt, setMerchantByReceipt] = useState({});
  const [notesByReceipt, setNotesByReceipt] = useState({});
  const [brokenPreviewIds, setBrokenPreviewIds] = useState({});
  const [selectedPreviewReceipt, setSelectedPreviewReceipt] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchReceipts = useCallback(async () => {
    setReceiptsLoading(true);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const response = isStaffScope
        ? await apiService.getStaffReceipts(params)
        : await apiService.getSuperuserReceipts(params);
      const nextReceipts = response.receipts || [];
      setReceipts(nextReceipts);
      setPointsByReceipt(prev => {
        const next = { ...prev };
        nextReceipts.forEach(receipt => {
          if (next[receipt.id] === undefined && !receipt.points_awarded && receipt.status !== 'declined') {
            next[receipt.id] = getDefaultPointsForReceipt(receipt);
          }
        });
        return next;
      });
      setSummary(response.summary || { pending: 0, approved: 0, declined: 0 });
      setTotalCount(response.count || 0);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch receipts';
      showToast(message, 'error');
    } finally {
      setReceiptsLoading(false);
    }
  }, [page, statusFilter, debouncedSearch, isStaffScope]);

  useEffect(() => {
    const init = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (isStaffScope ? !currentUser?.is_staff : !currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }

        if (isStaffScope) {
          const settingsResponse = await apiService.getStaffReceiptReviewSettings();
          setAutoApprove(!!settingsResponse.settings?.auto_approve);
        }
      } catch (error) {
        navigate('/login/');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate, isStaffScope]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (!loading) {
      fetchReceipts();
    }
  }, [loading, fetchReceipts]);

  useEffect(() => {
    if (!selectedPreviewReceipt) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedPreviewReceipt(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPreviewReceipt]);

  const updateAutoApprove = async (checked) => {
    setAutoApprove(checked);
    setSettingsSaving(true);
    try {
      const response = await apiService.updateStaffReceiptReviewSettings({ auto_approve: checked });
      setAutoApprove(!!response.settings?.auto_approve);
      showToast(checked ? 'Auto approve enabled' : 'Auto approve disabled');
    } catch (error) {
      setAutoApprove(!checked);
      const message = error.response?.data?.error || 'Failed to update setting';
      showToast(message, 'error');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleApprove = async (receipt) => {
    if (processingId) return;
    const points = pointsByReceipt[receipt.id] || '';
    const merchantName = (merchantByReceipt[receipt.id] || '').trim();
    const note = notesByReceipt[receipt.id] || '';
    const pointsValue = points === '' ? 0 : Number(points);
    if (pointsValue > 0 && !merchantName) {
      showToast('Merchant name is required when awarding points', 'error');
      return;
    }
    setProcessingId(receipt.id);
    try {
      const approveReceipt = isStaffScope ? apiService.approveStaffReceipt.bind(apiService) : apiService.approveSuperuserReceipt.bind(apiService);
      await approveReceipt(receipt.id, {
        points: points === '' ? 0 : points,
        merchant_name: merchantName,
        review_note: note,
      });
      showToast(points ? 'Receipt approved and points added' : 'Receipt approved');
      setPointsByReceipt(prev => ({ ...prev, [receipt.id]: '' }));
      setMerchantByReceipt(prev => ({ ...prev, [receipt.id]: '' }));
      setNotesByReceipt(prev => ({ ...prev, [receipt.id]: '' }));
      await fetchReceipts();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to approve receipt';
      showToast(message, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (receipt) => {
    if (processingId) return;
    const note = notesByReceipt[receipt.id] || '';
    setProcessingId(receipt.id);
    try {
      const declineReceipt = isStaffScope ? apiService.declineStaffReceipt.bind(apiService) : apiService.declineSuperuserReceipt.bind(apiService);
      await declineReceipt(receipt.id, { review_note: note });
      showToast('Receipt declined');
      setNotesByReceipt(prev => ({ ...prev, [receipt.id]: '' }));
      await fetchReceipts();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to decline receipt';
      showToast(message, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection={isStaffScope ? 'receipt-reviews' : 'superuser'} />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection={isStaffScope ? 'receipt-reviews' : 'superuser'} />
      <div className="admin-main">
        <div className="admin-content">
          {!isStaffScope && <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />}
          <h1 className="staff-page-header">Review Receipts</h1>
          <p className="staff-page-subheader">
            {isStaffScope
              ? 'Review receipts from fans at your school and grant points when approved.'
              : 'Approve receipts, decline cases, and grant points when review passes.'}
          </p>

          {isStaffScope && (
            <div style={{
            marginTop: '24px',
            padding: '18px',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            background: '#F8F8F8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontFamily: '"Baikal Book"', fontWeight: 900, fontSize: '16px' }}>Auto approve receipts</div>
              <div style={{ fontFamily: '"Baikal Book"', color: '#6b7280', marginTop: '4px', fontSize: '13px' }}>
                New receipts for your school become approved immediately. Points stay 0 until staff grants them.
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: '"Baikal Book"', cursor: settingsSaving ? 'not-allowed' : 'pointer' }}>
              <input
                type="checkbox"
                checked={autoApprove}
                disabled={settingsSaving}
                onChange={(e) => updateAutoApprove(e.target.checked)}
                style={{ width: '20px', height: '20px' }}
              />
              {autoApprove ? 'On' : 'Off'}
            </label>
            </div>
          )}

          <div style={{
            marginTop: '24px',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}>
            <input
              type="text"
              placeholder="Search by user, email, or notes"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...inputStyle, flex: '1 1 320px' }}
            />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              style={inputStyle}
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap', fontFamily: '"Baikal Book"', color: '#374151' }}>
            <span>Pending: {summary.pending || 0}</span>
            <span>Approved: {summary.approved || 0}</span>
            <span>Declined: {summary.declined || 0}</span>
          </div>

          {!receiptsLoading && (
            <Pagination
              position="top"
              page={page}
              totalItems={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}

          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {receiptsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading receipts...</div>
            ) : receipts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>No receipts found.</div>
            ) : receipts.map(receipt => (
              <div key={receipt.id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                background: 'white',
                padding: '16px',
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 1fr) minmax(260px, 1.4fr) minmax(260px, 1fr)',
                gap: '16px',
              }}>
                <div style={{ fontFamily: '"Baikal Book"' }}>
                  <div style={{ fontWeight: 900, fontSize: '16px' }}>{getUserName(receipt.user)}</div>
                  <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '13px' }}>{receipt.user?.email}</div>
                  <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '13px' }}>
                    {receipt.user?.active_school?.name || 'No school'}
                  </div>
                  <div style={{ marginTop: '12px', fontSize: '13px' }}>
                    <strong>Status:</strong> {getReceiptStatusLabel(receipt)}
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '13px' }}>
                    <strong>Submitted:</strong> {formatDateTime(receipt.created_at)}
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '13px' }}>
                    <strong>File:</strong> {receipt.original_filename || 'receipt'} · {formatFileSize(receipt.file_size)}
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '13px' }}>
                    <strong>Amount:</strong> {receipt.transaction_amount ? `$${Number(receipt.transaction_amount).toFixed(2)}` : 'N/A'}
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '13px' }}>
                    <strong>Transaction date:</strong> {receipt.transaction_date || 'N/A'}
                  </div>
                  {receipt.merchant_name && (
                    <div style={{ marginTop: '4px', fontSize: '13px' }}>
                      <strong>Merchant:</strong> {receipt.merchant_name}
                    </div>
                  )}
                </div>

                <div style={{ fontFamily: '"Baikal Book"' }}>
                  {isPreviewableReceiptImage(receipt) && !brokenPreviewIds[receipt.id] ? (
                    <button
                      type="button"
                      onClick={() => setSelectedPreviewReceipt(receipt)}
                      style={{
                        marginTop: '10px',
                        padding: 0,
                        width: '100%',
                        border: '1px solid #e5e7eb',
                        borderRadius: '10px',
                        background: '#f9fafb',
                        cursor: 'zoom-in',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <img
                        src={receipt.receipt_url}
                        alt="Receipt"
                        onError={() => setBrokenPreviewIds(prev => ({ ...prev, [receipt.id]: true }))}
                        style={{
                          width: '100%',
                          maxHeight: '240px',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        right: '10px',
                        bottom: '10px',
                        padding: '6px 10px',
                        borderRadius: '999px',
                        background: 'rgba(17, 24, 39, 0.78)',
                        color: 'white',
                        fontFamily: '"Baikal Book"',
                        fontSize: '12px',
                        fontWeight: 700,
                      }}>
                        View full
                      </span>
                    </button>
                  ) : (
                    <div style={{
                      marginTop: '10px',
                      padding: '16px',
                      borderRadius: '10px',
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      color: '#6b7280',
                      fontSize: '13px',
                    }}>
                      Preview unavailable.{' '}
                      <a
                        href={receipt.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb', fontWeight: 700 }}
                      >
                        Open receipt in new tab.
                      </a>
                    </div>
                  )}
                  <div style={{ marginTop: '10px', fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {receipt.notes || 'No notes'}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontFamily: '"Baikal Book"' }}>
                  <input
                    type="text"
                    placeholder="Merchant name"
                    value={merchantByReceipt[receipt.id] || ''}
                    disabled={receipt.points_awarded > 0 || receipt.status === 'declined' || processingId === receipt.id}
                    onChange={(e) => setMerchantByReceipt(prev => ({ ...prev, [receipt.id]: e.target.value }))}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder="Points to award"
                    value={pointsByReceipt[receipt.id] || ''}
                    disabled={receipt.points_awarded > 0 || receipt.status === 'declined' || processingId === receipt.id}
                    onChange={(e) => setPointsByReceipt(prev => ({ ...prev, [receipt.id]: e.target.value }))}
                    style={inputStyle}
                  />
                  <textarea
                    placeholder="Review note"
                    value={notesByReceipt[receipt.id] ?? receipt.review_note ?? ''}
                    disabled={receipt.status === 'declined' || processingId === receipt.id}
                    onChange={(e) => setNotesByReceipt(prev => ({ ...prev, [receipt.id]: e.target.value }))}
                    style={{ ...inputStyle, minHeight: '74px', resize: 'vertical' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleApprove(receipt)}
                      disabled={processingId === receipt.id || receipt.points_awarded > 0 || receipt.status === 'declined'}
                      style={{
                        ...buttonStyle,
                        background: '#10B981',
                        color: 'white',
                        opacity: processingId === receipt.id || receipt.points_awarded > 0 || receipt.status === 'declined' ? 0.5 : 1,
                      }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleDecline(receipt)}
                      disabled={processingId === receipt.id || receipt.points_awarded > 0 || receipt.status === 'declined'}
                      style={{
                        ...buttonStyle,
                        background: '#EF4444',
                        color: 'white',
                        opacity: processingId === receipt.id || receipt.points_awarded > 0 || receipt.status === 'declined' ? 0.5 : 1,
                      }}
                    >
                      Decline
                    </button>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '13px' }}>
                    Points awarded: {receipt.points_awarded || 0}
                  </div>
                  {receipt.reviewed_at && (
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>
                      Reviewed: {formatDateTime(receipt.reviewed_at)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {!receiptsLoading && (
            <Pagination
              page={page}
              totalItems={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}

          {toast && (
            <div style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              backgroundColor: toast.type === 'error' ? '#EF4444' : '#10B981',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1001,
              fontSize: '0.875rem',
              fontWeight: '500',
              maxWidth: '400px',
            }}>
              {toast.message}
              <button
                onClick={() => setToast(null)}
                style={{
                  marginLeft: '12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  padding: '0',
                  lineHeight: '1',
                }}
              >
                ×
              </button>
            </div>
          )}

          {selectedPreviewReceipt && (
            <div
              role="dialog"
              aria-modal="true"
              onClick={() => setSelectedPreviewReceipt(null)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2000,
                background: 'rgba(0, 0, 0, 0.86)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
              }}
            >
              <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '14px',
                  color: 'white',
                  fontFamily: '"Baikal Book"',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: '16px' }}>{getUserName(selectedPreviewReceipt.user)}</div>
                  <div style={{ opacity: 0.75, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedPreviewReceipt.original_filename || selectedPreviewReceipt.user?.email || 'Receipt'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <a
                    href={selectedPreviewReceipt.receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...buttonStyle,
                      display: 'inline-flex',
                      textDecoration: 'none',
                      background: 'white',
                      color: '#111827',
                    }}
                  >
                    Open in new tab
                  </a>
                  <button
                    type="button"
                    onClick={() => setSelectedPreviewReceipt(null)}
                    style={{
                      ...buttonStyle,
                      background: '#111827',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
              <img
                src={selectedPreviewReceipt.receipt_url}
                alt="Receipt full preview"
                onClick={(event) => event.stopPropagation()}
                onError={() => {
                  setBrokenPreviewIds(prev => ({ ...prev, [selectedPreviewReceipt.id]: true }));
                  setSelectedPreviewReceipt(null);
                }}
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 120px)',
                  objectFit: 'contain',
                  borderRadius: '12px',
                  background: 'white',
                  boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserReceiptReview;
